import * as React from 'react';
import styled from 'styled-components';

import { Dropdown } from '../Dropdown';
import { Button } from '../../primitives/Button';
import type { ButtonVariant } from '../../primitives/Button/buttonTypes';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { ImportFileInput } from '../../patterns/ImportFileInput';
import { palette } from '../../tokens/palette';
import { rem } from '../../tokens/spacing';

/* ─────────────────────────── Types ─────────────────────────────────────── */

export type TemplateActionMenuItem =
  | {
      type: 'action';
      label: string;
      semanticId?: string;
      onSelect: () => void;
    }
  | {
      type: 'file';
      label: string;
      semanticId: string;
      accept?: string;
      loading?: boolean;
      onFile: (event: React.ChangeEvent<HTMLInputElement>) => void;
      /** Legacy permission gate — wraps the file input when provided. */
      permission?: React.ReactElement;
    };

export interface TemplateActionMenuProps extends React.HTMLAttributes<HTMLDivElement> {
  triggerLabel: string;
  triggerSemanticId?: string;
  icon?: string;
  variant?: ButtonVariant;
  items: TemplateActionMenuItem[];
  /** Raise menu above modals — default true for table import toolbars. */
  elevated?: boolean;
}

/* ─────────────────────────── Styled ────────────────────────────────────── */

const StyledRoot = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const StyledMenuList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const StyledMenuListItem = styled.li`
  display: flex;
  font-style: normal;
  font-weight: 400;
  font-size: ${rem(14)};
  color: var(--ui-text, ${palette.Black});
  cursor: pointer;
  min-width: ${rem(140)};
  margin: 0 ${rem(10)};
  padding: ${rem(8)};
  text-align: left;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: var(--ui-background-muted, ${palette.OffWhite});
    font-weight: 600;
    border-radius: var(--ui-radius-sm, ${rem(4)});
  }
`;

const StyledFileMenuListItem = styled(StyledMenuListItem)`
  display: block;
`;

/* ─────────────────────────── Component ─────────────────────────────────── */

export const TemplateActionMenu = React.forwardRef<HTMLDivElement, TemplateActionMenuProps>(
  function TemplateActionMenu(
    {
      triggerLabel,
      triggerSemanticId,
      icon,
      variant = 'primary-outline',
      items,
      elevated = true,
      className,
      ...rest
    },
    ref,
  ) {
    const [open, setOpen] = React.useState(false);

    return (
      <StyledRoot ref={ref} className={className} {...rest}>
        <Dropdown.Root open={open} onOpenChange={setOpen} align="start">
          <Dropdown.Trigger asChild>
            <Button
              type="button"
              variant={variant}
              icon={icon}
              label={triggerLabel}
              semanticId={triggerSemanticId}
            />
          </Dropdown.Trigger>
          <Dropdown.Content elevated={elevated}>
            <StyledMenuList role="presentation">
              {items.map((item, index) => {
                const itemId = item.semanticId ?? `actionItem.${index}`;

                if (item.type === 'file') {
                  const fileInput = (
                    <ImportFileInput
                      semanticId={item.semanticId}
                      accept={item.accept}
                      label={item.label}
                      loading={item.loading}
                      onChange={item.onFile}
                    />
                  );

                  return (
                    <StyledFileMenuListItem key={item.semanticId}>
                      {item.permission
                        ? React.cloneElement(item.permission, { children: fileInput })
                        : fileInput}
                    </StyledFileMenuListItem>
                  );
                }

                if (item.type === 'action') {
                  return (
                    <StyledMenuListItem
                      key={itemId}
                      {...(item.semanticId
                        ? getSemanticAttributes(item.semanticId)
                        : { id: itemId })}
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        item.onSelect();
                        setOpen(false);
                      }}
                    >
                      {item.label}
                    </StyledMenuListItem>
                  );
                }

                return null;
              })}
            </StyledMenuList>
          </Dropdown.Content>
        </Dropdown.Root>
      </StyledRoot>
    );
  },
);

TemplateActionMenu.displayName = 'TemplateActionMenu';
